import { memo, KeyboardEvent, MouseEvent } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { AnalyticsCommonTypes } from '@frontend/api-analytics';
import { getDisplayName } from '@frontend/get-display-name';
import { useTranslation } from '@frontend/i18n';
import { useForceTray } from '@frontend/panel-engine';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { Photos } from '@frontend/photos';
import { useContactPanelStore } from '@frontend/shared';
import { useSlidePanelStore } from '@frontend/slide-panel';
import { theme } from '@frontend/theme';
import { Text, styles as dsStyles } from '@frontend/design-system';
import { LocationChip } from './location-chip';
import { StatusIcon } from './status-icon';

export const UserCard = memo(
  ({
    backLinkAction,
    firstName = '',
    lastName = '',
    preferredName = '',
    locationId,
    locationName,
    openProfileOnClick = true,
    phoneNumber,
    rightElement,
    showOnlyName,
    style,
    time,
    userId,
    openPanelInTray,
    variant = 'default',
    unavailableDataText,
    ...rest
  }: AnalyticsCommonTypes.UserCard) => {
    const { t } = useTranslation('analytics');
    const { setShow } = useSlidePanelStore();
    const { setPersonId } = useContactPanelStore();
    const { enableForceTray } = useForceTray();

    const { fullName, legalName } = getDisplayName({ firstName, lastName, preferredName });
    const isClickable = openProfileOnClick && !!userId;
    const hasNameOrNumber = !!fullName || !!phoneNumber;
    const isCompact = variant === 'compact';

    const handlUserClick = (event?: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
      if (isClickable && event) {
        event.preventDefault();
        event.stopPropagation();
        if (openPanelInTray) {
          enableForceTray();
        }
        if (typeof backLinkAction === 'function') {
          setPersonId(userId, false, locationId);
          setShow(true, 'contact', {
            backlink: backLinkAction,
          });
        } else {
          // Using set show to hide the previous panel so we can remove the backlink
          setShow(false, 'contact');
          setPersonId(userId, true, locationId);
        }
      }
    };

    return (
      <div css={[styles.mainWrapper, isCompact && styles.compact]} style={style} {...rest}>
        <div
          css={styles.contactWrapper}
          onClick={handlUserClick}
          role={isClickable ? 'button' : undefined}
          style={{ cursor: isClickable ? 'pointer' : 'default' }}
        >
          <Photos.ContactProfilePhoto
            size={isCompact ? 'small' : undefined}
            disableClick={!isClickable}
            locationId={locationId}
            name={fullName || t('Unknown')}
            title={legalName}
            personId={userId || ''}
            onClick={handlUserClick}
          />
          <div className='content-wrapper'>
            <div className='name-wrapper'>
              <Text
                className='name-text'
                color={hasNameOrNumber ? 'default' : 'subdued'}
                css={dsStyles.truncate}
                size='medium'
                style={{ flex: 'none', maxWidth: rightElement ? `calc(100% - 16px)` : '100%' }}
              >
                {fullName ? (
                  <>
                    {!!firstName && (
                      <Text as='span' weight='bold'>
                        {`${firstName} `}
                      </Text>
                    )}
                    {lastName ?? ''}
                  </>
                ) : phoneNumber ? (
                  formatPhoneNumber(phoneNumber)
                ) : (
                  unavailableDataText ?? t('Unavailable Data')
                )}
              </Text>
              <StatusIcon statusIcon={rightElement} />
            </div>

            {!showOnlyName && (
              <Text color='light' size='medium'>
                {phoneNumber ? formatPhoneNumber(phoneNumber) : t('Unavailable Data')}
              </Text>
            )}
            {time && (
              <Text size='medium' className='time'>
                {dayjs(time.value).format(time.customFormat ?? 'MMM D, YYYY, h:mm A')}
              </Text>
            )}
          </div>
        </div>
        <LocationChip locationName={locationName} />
      </div>
    );
  }
);

UserCard.displayName = 'UserCard';

const styles = {
  mainWrapper: css`
    align-items: center;
    display: flex;
    gap: ${theme.spacing(1)};
    justify-content: space-between;
    max-width: 100%;

    .content-wrapper {
      width: calc(100% - ${theme.spacing(6)});
    }
  `,

  contactWrapper: css`
    align-items: center;
    display: flex;
    gap: ${theme.spacing(1)};
    max-width: 100%;

    .avatar-wraper {
      align-items: center;
      background-color: ${theme.colors.neutral30};
      border-radius: ${theme.borderRadius.full};
      color: ${theme.colors.white};
      display: flex;
      justify-content: center;
      overflow: hidden;

      span {
        color: inherit;
        font-weight: ${theme.font.weight.bold};
      }
    }

    .name-wrapper {
      align-items: center;
      display: flex;
      gap: ${theme.spacing(1)};
      position: relative;
    }
  `,

  compact: css`
    width: calc(220px - ${theme.spacing(2)});

    .content-wrapper {
      width: calc(100% - ${theme.spacing(3)});
    }

    .name-text {
      font-size: ${theme.fontSize(14)};
    }
  `,
};
